import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';

const API_BASE = process.env.REACT_APP_API_URL;

export default function WorkOrderNew() {
  const { getToken } = useAuth();
  const navigate = useNavigate();

  const [flows, setFlows]           = useState([]);
  const [selectedFlow, setSelectedFlow] = useState('');
  const [serialInput, setSerialInput]   = useState('');
  const [serials, setSerials]           = useState([]);
  const [notes, setNotes]               = useState('');
  const [woNumber, setWoNumber]         = useState('');
  const [saving, setSaving]             = useState(false);
  const [error, setError]               = useState('');

  // Load released process flows
  useEffect(() => {
    getToken().then(token =>
      window.fetch(`${API_BASE}/quality/process-flows?status=Released`, {
        headers: { Authorization: `Bearer ${token}` },
      }).then(r => r.json()).then(d => { if (d.ok) setFlows(d.flows || []); })
    );
  }, [getToken]);

  // Auto-fill work order number
  useEffect(() => {
    getToken().then(token =>
      window.fetch(`${API_BASE}/quality/next-doc-number?prefix=WO-NIN-`, {
        headers: { Authorization: `Bearer ${token}` },
      }).then(r => r.json()).then(d => { if (d.documentNumber) setWoNumber(d.documentNumber); })
    );
  }, [getToken]);

  const addSerial = () => {
    const v = serialInput.trim();
    if (!v || serials.includes(v)) return;
    setSerials(s => [...s, v]);
    setSerialInput('');
  };

  const addBulk = text => {
    const lines = text.split(/[\n,;\s]+/).map(s => s.trim()).filter(Boolean);
    setSerials(prev => {
      const next = [...prev];
      lines.forEach(l => { if (!next.includes(l)) next.push(l); });
      return next;
    });
    setSerialInput('');
  };

  const submit = async () => {
    if (!selectedFlow || serials.length === 0 || !woNumber.trim()) {
      setError('Process flow, work order number, and at least one serial number are required.');
      return;
    }
    setSaving(true); setError('');
    try {
      const token = await getToken();
      const res = await window.fetch(`${API_BASE}/quality/work-orders`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ workOrderNumber: woNumber, flowId: selectedFlow, serialNumbers: serials, notes }),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || 'Failed to create work order');
      navigate(`/quality/execution/${data.order._id}`);
    } catch (err) { setError(err.message); } finally { setSaving(false); }
  };

  const inp = { background:'var(--ni-panel)', border:'1px solid var(--ni-border-soft)',
    color:'var(--ni-text)', borderRadius:6, padding:'8px 12px', fontSize:13, width:'100%', boxSizing:'border-box' };
  const labelS = { display:'block', fontSize:11, fontWeight:700, letterSpacing:'0.06em',
    textTransform:'uppercase', color:'var(--ni-text-muted)', marginBottom:5 };

  return (
    <div style={{ maxWidth:620 }}>
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:24 }}>
        <button onClick={() => navigate('/quality/execution')}
          style={{ background:'none', border:'none', color:'var(--ni-text-muted)', cursor:'pointer', fontSize:18 }}>←</button>
        <h2 style={{ margin:0, fontSize:20, fontWeight:800 }}>New Work Order</h2>
      </div>

      {error && <div style={{ background:'#EC212420', border:'1px solid #EC212440', borderRadius:8,
        padding:'10px 14px', color:'#EC2124', fontSize:13, marginBottom:16 }}>{error}</div>}

      <div style={{ display:'flex', flexDirection:'column', gap:18 }}>

        <div>
          <label style={labelS}>Work Order Number</label>
          <input value={woNumber} onChange={e => setWoNumber(e.target.value)} style={inp} placeholder="WO-NIN-001" />
        </div>

        <div>
          <label style={labelS}>Process Flow *</label>
          <select value={selectedFlow} onChange={e => setSelectedFlow(e.target.value)} style={inp}>
            <option value="">— Select a released process flow —</option>
            {flows.map(f => (
              <option key={f._id} value={f._id}>{f.documentNumber} — {f.name}</option>
            ))}
          </select>
          {flows.length === 0 && (
            <div style={{ fontSize:12, color:'var(--ni-text-muted)', marginTop:5 }}>
              No released process flows found. Release a flow first.
            </div>
          )}
        </div>

        <div>
          <label style={labelS}>Serial Numbers *</label>
          <div style={{ display:'flex', gap:8, marginBottom:8 }}>
            <input value={serialInput} onChange={e => setSerialInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addSerial(); } }}
              style={{ ...inp, flex:1 }} placeholder="e.g. SN-001 (press Enter to add)" />
            <button onClick={addSerial} style={{ background:'#60a5fa22', border:'1px solid #60a5fa44',
              color:'#60a5fa', borderRadius:6, padding:'8px 14px', fontSize:13, cursor:'pointer', whiteSpace:'nowrap' }}>Add</button>
          </div>
          <div style={{ marginBottom:6 }}>
            <button onClick={() => {
              const text = window.prompt('Paste serial numbers (comma, newline, or space separated):');
              if (text) addBulk(text);
            }} style={{ background:'none', border:'1px solid var(--ni-border-soft)', color:'var(--ni-text-muted)',
              borderRadius:6, padding:'5px 12px', fontSize:12, cursor:'pointer' }}>
              Paste bulk…
            </button>
          </div>
          {serials.length > 0 && (
            <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
              {serials.map(sn => (
                <span key={sn} style={{ display:'flex', alignItems:'center', gap:4, background:'#60a5fa18',
                  border:'1px solid #60a5fa33', borderRadius:6, padding:'3px 10px', fontSize:12 }}>
                  <span style={{ fontFamily:'monospace' }}>{sn}</span>
                  <button onClick={() => setSerials(s => s.filter(x => x !== sn))}
                    style={{ background:'none', border:'none', color:'#EC2124', cursor:'pointer', fontSize:13, padding:0, lineHeight:1 }}>×</button>
                </span>
              ))}
            </div>
          )}
          {serials.length > 0 && (
            <div style={{ fontSize:12, color:'var(--ni-text-muted)', marginTop:6 }}>{serials.length} device{serials.length!==1?'s':''}</div>
          )}
        </div>

        <div>
          <label style={labelS}>Notes (optional)</label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3}
            style={{ ...inp, resize:'vertical' }} placeholder="Batch description, special instructions…" />
        </div>

        <div style={{ display:'flex', gap:10 }}>
          <button onClick={submit} disabled={saving || !selectedFlow || serials.length === 0}
            style={{ background:'#60a5fa', color:'#000', border:'none', borderRadius:6,
              padding:'10px 24px', fontWeight:700, fontSize:13, cursor:'pointer',
              opacity: (!selectedFlow || serials.length === 0) ? 0.5 : 1 }}>
            {saving ? 'Creating…' : 'Create Work Order'}
          </button>
          <button onClick={() => navigate('/quality/execution')}
            style={{ background:'transparent', border:'1px solid var(--ni-border-soft)', color:'var(--ni-text-muted)',
              borderRadius:6, padding:'10px 16px', fontSize:13, cursor:'pointer' }}>Cancel</button>
        </div>
      </div>
    </div>
  );
}
